import { z } from 'zod';
import { ACCESS_DENIAL_REASONS, ACCESS_DIRECTIONS, ACCESS_RESULTS, BOOKING_STATUSES, CREDENTIAL_STATUSES, CREDENTIAL_TYPES, MEMBER_STATUSES, MEMBERSHIP_STATUSES, MEMBERSHIP_TRANSITIONS, PAYMENT_STATUSES } from '@bulk/types';
import { dateOnlySchema, idSchema, localeSchema, patchOf, searchQuerySchema, sortOrderSchema } from './common';
import { memberTagRefSchema } from './member-tags';

export const genderSchema = z.enum(['MALE', 'FEMALE', 'OTHER', 'UNSPECIFIED']);

export const createMemberSchema = z.object({
  email: z.email().toLowerCase().nullable().optional(),
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  phone: z.string().trim().max(32).nullable().optional(),
  dateOfBirth: dateOnlySchema.nullable().optional(),
  gender: genderSchema.default('UNSPECIFIED'),
  nationalId: z.string().trim().max(40).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  locale: localeSchema.optional(),
  createLogin: z.boolean().default(false),
});
export type CreateMemberInput = z.infer<typeof createMemberSchema>;

export const updateMemberSchema = patchOf(createMemberSchema.omit({ createLogin: true })).extend({
  status: z.enum(MEMBER_STATUSES).optional(),
  smsNotifications: z.boolean().optional().describe('Whether service notices (classes, membership, sign-in link) also go to the phone by SMS'),
  smsOffers: z.boolean().optional().describe("Record the member's answer, given at the desk, to offers by SMS; each change is kept with who recorded it"),
  emailOffers: z.boolean().optional().describe("Record the member's answer, given at the desk, to offers by email; each change is kept with who recorded it"),
});
export type UpdateMemberInput = z.infer<typeof updateMemberSchema>;

export const memberSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  userId: idSchema.nullable(),
  memberNumber: z.string(),
  email: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  gender: genderSchema,
  nationalId: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(MEMBER_STATUSES),
  photoAssetId: idSchema.nullable(),
  photoUrl: z.string().nullable().describe('A signed link to the private photo; it expires, so fetch the member again rather than storing it'),
  smsNotifications: z.boolean().describe('Service notices (classes, membership, sign-in link) also go to the phone by SMS when the gym has an SMS account'),
  smsOffers: z.boolean().describe('The member agreed to offers by SMS (marketing); off until they say yes'),
  emailOffers: z.boolean().describe('The member agreed to offers by email (marketing); off until they say yes'),
  tags: z.array(memberTagRefSchema).describe("The gym's tags on the member, for staff who read members; always empty for the member themself"),
  corporateClient: z.object({ id: idSchema, name: z.string() }).nullable().describe("The company whose roster the member is on, which pays for what it buys them; for staff who read members while the corporate module is on"),
  joinedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MemberDto = z.infer<typeof memberSchema>;

export const memberLoginSchema = z.object({
  status: z.enum(['NONE', 'PENDING', 'WAITING_CONFIRMATION', 'ACTIVE']),
  invitation: z.object({ sentAt: z.string(), expiresAt: z.string() }).nullable(),
});
export type MemberLoginDto = z.infer<typeof memberLoginSchema>;

export const MEMBER_LIST_SORTS = ['createdAt', 'joinedAt', 'memberNumber', 'name', 'lastName', 'email', 'phone', 'status'] as const;

export const memberListQuerySchema = searchQuerySchema.extend({
  status: z.enum(MEMBER_STATUSES).optional(),
  membershipStatus: z.string().optional(),
  tagIds: z
    .string()
    .transform((value) => value.split(',').filter(Boolean))
    .pipe(z.array(idSchema).max(20))
    .optional()
    .describe('Comma-separated tag ids: members carrying at least one of them'),
  segmentId: idSchema.optional().describe('Only the members of this saved segment'),
  sort: z.enum(MEMBER_LIST_SORTS).default('createdAt').describe('`name` and `lastName` both order by last name, then first name; empty values come last in either direction'),
  order: sortOrderSchema.default('desc'),
});
export type MemberListQuery = z.infer<typeof memberListQuerySchema>;

export const memberExportQuerySchema = memberListQuerySchema.omit({ page: true, pageSize: true });
export type MemberExportQuery = z.infer<typeof memberExportQuerySchema>;

export const createCredentialSchema = z.object({
  type: z.enum(CREDENTIAL_TYPES),
  value: z.string().trim().min(1).max(200),
  label: z.string().trim().max(80).nullable().optional(),
});
export type CreateCredentialInput = z.infer<typeof createCredentialSchema>;

export const credentialSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  memberId: idSchema,
  type: z.enum(CREDENTIAL_TYPES),
  valueMasked: z.string(),
  label: z.string().nullable(),
  status: z.enum(CREDENTIAL_STATUSES),
  issuedAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type CredentialDto = z.infer<typeof credentialSchema>;

export const updateCredentialSchema = z.object({
  status: z.enum(CREDENTIAL_STATUSES).optional(),
  label: z.string().trim().max(80).nullable().optional(),
});

export const MEMBER_FLAGS = ['EXPIRES_SOON', 'NO_ACTIVE_MEMBERSHIP', 'PAUSED', 'UNPAID_ORDER', 'NO_CARD', 'DENIED_RECENTLY', 'VISIT_LIMIT_NEAR'] as const;
export type MemberFlag = (typeof MEMBER_FLAGS)[number];

const timelineBase = { at: z.string(), tone: z.enum(['good', 'bad', 'neutral']) };

export const memberTimelineItemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ACCESS'), ...timelineBase, result: z.enum(ACCESS_RESULTS), direction: z.enum(ACCESS_DIRECTIONS), reason: z.enum(ACCESS_DENIAL_REASONS).nullable(), readerName: z.string().nullable() }),
  z.object({ kind: z.literal('PAYMENT'), ...timelineBase, status: z.enum(PAYMENT_STATUSES), amountMinor: z.number().int(), currency: z.string(), description: z.string().nullable() }),
  z.object({ kind: z.literal('BOOKING'), ...timelineBase, status: z.enum(BOOKING_STATUSES), className: z.string(), startsAt: z.string() }),
  z.object({ kind: z.literal('MEMBERSHIP'), ...timelineBase, transition: z.enum(MEMBERSHIP_TRANSITIONS), reason: z.string().nullable() }),
]);
export type MemberTimelineItem = z.infer<typeof memberTimelineItemSchema>;

export const memberOverviewSchema = z.object({
  memberId: idSchema,
  timezone: z.string(),
  currentMembership: z
    .object({
      id: idSchema,
      planName: z.string(),
      status: z.string(),
      startsAt: z.string().nullable(),
      endsAt: z.string().nullable(),
      daysLeft: z.number().int().nullable().describe("Whole days until the membership ends, counted in the gym's time zone; 0 on the last day"),
      durationDays: z.number().int().nullable(),
      visitsUsed: z.number().int(),
      visitLimit: z.number().int().nullable(),
      pausedAt: z.string().nullable(),
      autoRenew: z.boolean(),
    })
    .nullable(),
  visits: z.object({
    last30Days: z.number().int(),
    thisMonth: z.number().int(),
    lastVisitAt: z.string().nullable(),
    insideNow: z.boolean(),
    averagePerWeek: z.number(),
    daily: z.array(z.object({ date: z.string(), visits: z.number().int() })).describe('The last 30 local days, oldest first'),
  }),
  money: z
    .object({
      lifetimePaidMinor: z.number().int(),
      refundedMinor: z.number().int(),
      openOrdersMinor: z.number().int(),
      lastPaymentAt: z.string().nullable(),
      currency: z.string(),
      /** The branches whose payments are counted, for staff who work only at some; null for all of them. Open orders are never scoped. */
      branches: z.array(z.string()).nullable(),
    })
    .nullable(),
  classes: z
    .object({
      upcomingBookings: z.number().int(),
      attendedLast30Days: z.number().int(),
      noShowsLast30Days: z.number().int(),
      nextBooking: z.object({ sessionId: idSchema, className: z.string(), startsAt: z.string() }).nullable(),
    })
    .nullable(),
  credentials: z.object({ active: z.number().int(), lastUsedAt: z.string().nullable() }),
  flags: z.array(z.enum(MEMBER_FLAGS)),
  timeline: z.array(memberTimelineItemSchema),
});
export type MemberOverviewDto = z.infer<typeof memberOverviewSchema>;

export const MAX_VISIT_CALENDAR_DAYS = 371;

export const memberVisitCalendarQuerySchema = z.object({
  days: z.coerce.number().int().min(7).max(MAX_VISIT_CALENDAR_DAYS).default(MAX_VISIT_CALENDAR_DAYS).describe("How many of the gym's local days to cover, ending today"),
});
export type MemberVisitCalendarQuery = z.infer<typeof memberVisitCalendarQuerySchema>;

export const memberVisitCalendarSchema = z.object({
  memberId: idSchema,
  timezone: z.string(),
  from: dateOnlySchema,
  to: dateOnlySchema,
  days: z
    .array(
      z.object({
        date: dateOnlySchema,
        visits: z.number().int(),
        minutes: z.number().int().describe('Time inside on visits that started this day and have an exit'),
        withoutExit: z.number().int().describe('Visits that started this day with no exit recorded, so their length is unknown'),
      }),
    )
    .describe("Only the gym's local days with at least one visit, oldest first"),
  totals: z.object({
    visits: z.number().int(),
    activeDays: z.number().int(),
    minutes: z.number().int(),
    averageMinutes: z.number().int().nullable().describe('Over visits with an exit'),
    withoutExit: z.number().int(),
    currentStreak: z.number().int().describe('Consecutive days with a visit, ending today, or yesterday when today has none yet'),
    longestStreak: z.number().int(),
    busiestWeekday: z.number().int().min(1).max(7).nullable().describe('ISO weekday with the most visits: 1 Monday … 7 Sunday'),
    usualHour: z.number().int().min(0).max(23).nullable().describe('Local hour most visits start in'),
    lastVisitAt: z.string().nullable(),
  }),
});
export type MemberVisitCalendarDto = z.infer<typeof memberVisitCalendarSchema>;

export const memberActivityQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional().describe("A calendar year in the gym's time zone, from the year the member joined to this one; this year when omitted"),
});
export type MemberActivityQuery = z.infer<typeof memberActivityQuerySchema>;

export const memberActivityYearSchema = memberVisitCalendarSchema.extend({
  year: z.number().int(),
  firstYear: z.number().int().describe('The year the member joined'),
  lastYear: z.number().int().describe('This year'),
  coverage: z
    .array(
      z.object({
        membershipId: idSchema,
        planName: z.string(),
        status: z.enum(MEMBERSHIP_STATUSES).describe("The membership's status now"),
        from: dateOnlySchema,
        to: dateOnlySchema,
      }),
    )
    .describe('Spans of local days within from…to on which a membership could be used, oldest first. Frozen and suspended days are left out, and a cancelled membership ends the day it was cancelled; a day in no span had no membership.'),
}).describe('A year of visits (from is the later of 1 January and the day the member joined, to the earlier of 31 December and today) and the membership that covered each day');
export type MemberActivityYearDto = z.infer<typeof memberActivityYearSchema>;
